import { ReceiveMethodEnum } from '@goparrot/common';
import { DelayedOrdersUtils } from '@goparrot/store-v2-sdk';
import type { ReadPlacePickerStoreCardDto, ReadPlacePickerStoreCardStatusButtonTextDto, ReadPlacePickerStoreDto } from '@goparrot/webstore-sdk';
import get from 'lodash/get';
import qs from 'qs';

import { locationSelectorFacade } from '../api';
import type { IPlacePickerStores, LocationBounds, MapDetails } from '../contexts/LocationStoresProvider';

const ONE_METER_IN_MILE = 0.000621371192;

export const getMiles = (distanceInMeters: number) => Number((distanceInMeters * ONE_METER_IN_MILE).toFixed(2));

export function closeWebview(error: string) {
  try {
    //@ts-ignore
    if (window.MessengerExtensions) {
      //@ts-ignore
      window.MessengerExtensions.requestCloseBrowser(
        function success() {
          console.log('webview closed');
          if (error) console.error(error);
        },

        function error(err: any) {
          console.log(err);
        },
      );
    } else {
      window.close();
    }
  } catch (err) {
    window && window.close();
  }
}

export const getOrderAheadMessage = (store: IPlacePickerStores, data: ReadPlacePickerStoreCardDto) => {
  const isPickup = store?.diningOptionsConfig?.TAKE_OUT?.isEnabled || store?.diningOptionsConfig?.CURBSIDE?.isEnabled;

  if (!store.humanReadableData.isOpen) {
    const orderAheadAvailabilityForMethods = DelayedOrdersUtils.isOrderAheadAvailable(store);
    if (
      (isPickup && orderAheadAvailabilityForMethods[ReceiveMethodEnum.PICKUP]) ||
      (store?.delivery?.isEnabled && orderAheadAvailabilityForMethods[ReceiveMethodEnum.DELIVERY])
    ) {
      return get(data, 'orderAheadMessage.message', 'Order Ahead is available.');
    }
  }
};

/* eslint-enable */
export const getStoreStatus = ({ order, humanReadableData }: IPlacePickerStores, buttonText?: ReadPlacePickerStoreCardStatusButtonTextDto) => {
  if (!order?.isActive || order?.isEmergencyStop) {
    return buttonText?.viewMenu || 'View Menu';
  }
  if (!humanReadableData.isOpen) {
    return humanReadableData.openingTime !== 'never'
      ? `${buttonText?.opensPrefix || 'Opens'} ${humanReadableData.openingTime}`
      : buttonText?.opensNever || 'Opens soon';
  }

  return buttonText?.orderNow || 'Order now';
};

export const hideStoreField = (placePickerStore: ReadPlacePickerStoreDto, field: string) => {
  return get(placePickerStore, `popup.hide.${field}`, false);
};

/**
 * Filters stores based on
 * @param {*} props
 */

interface GetVisibleLocationForSelectedLocationProps {
  selectedLocation: LocationBounds | null;
  filteredStores: IPlacePickerStores[];
  useDeliveryAlgorithm: boolean;
  bounds?: google.maps.LatLngBounds | LocationBounds['viewport'] | null;
}
export function getVisibleLocationForSelectedLocation({
  selectedLocation,
  filteredStores = [],
  useDeliveryAlgorithm,
  bounds,
}: GetVisibleLocationForSelectedLocationProps) {
  if (selectedLocation && selectedLocation.viewport && usesDeliveryAlgorithm(useDeliveryAlgorithm)) {
    return filteredStores.filter((store) => !!store.deliveryRadiusContainsLocation);
  } else if (selectedLocation && selectedLocation.viewport && !usesDeliveryAlgorithm(useDeliveryAlgorithm)) {
    return filteredStores.filter((store) => Boolean(store.location));
  } else {
    return filteredStores
      .filter((store) => Boolean(store.location))
      .filter(() => {
        return !!bounds;
      });
  }
}

export const usesDeliveryAlgorithm = (useDeliveryAlgorithm: boolean) => {
  const { dpdefault } = qs.parse(window.location.search, { ignoreQueryPrefix: true });
  if (useDeliveryAlgorithm) return true;

  return ReceiveMethodEnum.DELIVERY === dpdefault;
};

/**
 * Takes all filtered stores and tries to calculte distance to selectedLocation
 * In case of delivery algorith it marks if store can delivery to selectedLocation
 * If there is no selectedLocation, then it tries to calculate distance from sortingCenter if it's available
 */

interface OnLocationChangeRecalculateProps {
  filteredStores: IPlacePickerStores[];
  selectedLocation: LocationBounds | null;
  mapDetails: MapDetails;
  filterStatus: string;
}

export async function onLocationChangeRecalculate({ filteredStores, selectedLocation, mapDetails, filterStatus }: OnLocationChangeRecalculateProps) {
  const { google } = window;
  if (selectedLocation && selectedLocation.location) {
    const location = new google.maps.LatLng(selectedLocation.location);
    const isDeliveryFilter = ReceiveMethodEnum.DELIVERY === filterStatus;

    if (usesDeliveryAlgorithm(isDeliveryFilter)) {
      const deliverableStores = await locationSelectorFacade.getDeliverableStores(location.lat(), location.lng());

      return filteredStores
        .map((store) => {
          const deliverableStore = deliverableStores.find((deliveryStore) => deliveryStore.storeId === store.storeId);

          store.deliveryRadiusContainsLocation = !!deliverableStore;

          store.distance = Number(deliverableStore?.distance?.toFixed(2));
          return store;
        })
        .filter((store) => {
          return store.deliveryRadiusContainsLocation;
        });
    }

    return filteredStores.map((store) => {
      const { latitude: lat, longitude: lng } = store.location;
      const storeAddress = new google.maps.LatLng({ lat, lng });

      store.distance = selectedLocation.location === null ? null : getMiles(google.maps.geometry.spherical.computeDistanceBetween(location, storeAddress));

      return store;
    });
  } else if (!selectedLocation && mapDetails && mapDetails.sortingCenter && mapDetails.sortingCenter.lng && mapDetails.sortingCenter.lat) {
    const center = mapDetails.sortingCenter as { lat: number; lng: number };
    const location = new google.maps.LatLng(center);

    return filteredStores.map((store) => {
      const { latitude: lat, longitude: lng } = store.location;
      const storeAddress = new google.maps.LatLng({ lat, lng });

      store.distance = getMiles(google.maps.geometry.spherical.computeDistanceBetween(location, storeAddress));

      return store;
    });
  } else {
    return filteredStores.map((store) => {
      store.distance = undefined;
      return store;
    });
  }
}

export const getClosestLocation = ({ filteredStores, map }: { filteredStores: IPlacePickerStores[]; map: google.maps.Map }) => {
  const { google } = window;
  const location = map.getCenter();

  return filteredStores
    .map((store) => {
      const { latitude: lat, longitude: lng } = store.location;
      const storeAddress = new google.maps.LatLng({ lat, lng });

      store.distance = location ? getMiles(google.maps.geometry.spherical.computeDistanceBetween(location, storeAddress)) : 0;
      return store;
    })

    .sort((storeA, storeB) => {
      if (!storeA.distance && !storeB.distance) {
        return 0;
      } else if (!storeA.distance) {
        return 1;
      } else if (!storeB.distance) {
        return -1;
      } else {
        return storeA.distance - storeB.distance;
      }
    });
};
